import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { HomeOutlined } from "@ant-design/icons";
import { useBreadcrumbs } from "../../../context/breadcrumbs";
import Table from "../../Common/Table";
import {
  ProColumns,
  RequestData,
  RequestOptionsType,
} from "@ant-design/pro-components";
import clsx from "clsx";
import { NavLink, useNavigate, useSearchParams } from "react-router";
import { DatePicker } from "antd";
import LogHistoryViewModal from "./View";
import { LoggerTableRow, useLoggerTableGet } from "../../../hooks/api/loggers";
import { SortOrder } from "antd/es/table/interface";
import { queryFilterParams, getSorterParams } from "../../../utils";
import { dateTimeFormat } from "../../../contstant";
import dayjs from "dayjs";

function ToolBar({ onCreate }: { onCreate: () => void }) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return [];
}

let modifierCache: RequestOptionsType[] | null = null;
let objectCache: RequestOptionsType[] | null = null;

export default function LogHistoryContent(): React.ReactNode | null {
  const [, setSearchParams] = useSearchParams();
  const [tableKey, setTableKey] = useState<number>(() => Date.now());
  const [createOpen, setCreateOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<any | null>(null);
  const { t, i18n } = useTranslation();
  const { setBreadcrumbs } = useBreadcrumbs();
  const loggers = useLoggerTableGet();

  // Static trail, no derivation: the shell resets the breadcrumbs on every
  // screen and only the screen itself fills them in. Building them from the
  // route is a developer's job — see DEVELOPER_TASKS.md.
  useEffect(() => {
    setBreadcrumbs([
      { title: <HomeOutlined />, href: "/" },
      { title: t("routes.logHistory") },
    ]);
  }, [i18n.language, t]);

  const loadModifierOptions = async (
    loggers: ReturnType<typeof useLoggerTableGet>,
  ): Promise<RequestOptionsType[]> => {
    if (modifierCache) return modifierCache;

    const response = await loggers.fetch({ page: 1, pageSize: 100 });
    if (!response?.data) return [];

    const map = new Map<string, RequestOptionsType>();
    response.data.forEach((item) => {
      if (item.user?.id) {
        map.set(item.user.id, {
          label: item.user.fullName,
          value: item.user.id,
        });
      }
    });

    modifierCache = Array.from(map.values());
    return modifierCache;
  };

  const loadObjectOptions = async (
    loggers: ReturnType<typeof useLoggerTableGet>,
  ): Promise<RequestOptionsType[]> => {
    if (objectCache) return objectCache;

    const response = await loggers.fetch({ page: 1, pageSize: 100 });
    if (!response?.data) return [];

    const map = new Map<string, RequestOptionsType>();
    response.data.forEach((item) => {
      [item.after, item.before].forEach((u) => {
        if (u?.id) {
          map.set(u.id, {
            label: u.email,
            value: u.id,
          });
        }
      });
    });

    objectCache = Array.from(map.values());
    return objectCache;
  };

  const columns: ProColumns[] = [
    {
      title: t("pages.logHistory.columns.modifier"),
      dataIndex: "user",
      key: "user",
      sorter: true,
      hideInSearch: true,
      render: (_, record) => (
        <span className="capitalize-first-letter">
          {" "}
          {record.user?.fullName ?? "-"}
        </span>
      ),
    },
    {
      title: t("pages.logHistory.columns.modifier"),
      dataIndex: ["user", "fullName"],
      key: "modifier",
      sorter: true,
      hideInTable: true,
      valueType: "select",
      request: () => loadModifierOptions(loggers),
      fieldProps: {
        showSearch: true,
        filterOption: true,
      },
      search: {
        transform: (value) => ({
          userId: value,
        }),
      },
    },
    {
      title: t("pages.logHistory.columns.date"),
      dataIndex: "createdAt",
      key: "createdAt",
      hideInSearch: true,
      sorter: true,
      renderText: (text) => dayjs(text).format(dateTimeFormat),
    },
    {
      title: t("pages.logHistory.columns.date"),
      dataIndex: "createdAt",
      key: "createdAt",
      sorter: true,
      valueType: "dateRange",
      hideInTable: true,
      search: {
        transform: (value?: [string, string]) => {
          if (!value || value.length !== 2) return {};

          return {
            createdFrom: value[0],
            createdTo: value[1],
          };
        },
      },
    },
    {
      title: t("pages.logHistory.columns.entity"),
      dataIndex: "entity",
      key: "entity",
      sorter: true,
      valueType: "select",
      valueEnum: {
        User: { text: t("pages.logHistory.entities.user") },
        Order: { text: t("pages.logHistory.entities.order") },
        Product: { text: t("pages.logHistory.entities.product") },
      },
      render: (_, record) => (
        <span className="capitalize-first-letter">{record.entity}</span>
      ),
    },
    {
      title: t("pages.logHistory.columns.operation"),
      dataIndex: "event",
      key: "event",
      sorter: true,
      valueEnum: {
        Create: { text: t("pages.logHistory.operations.create") },
        Edit: { text: t("pages.logHistory.operations.edit") },
        Delete: { text: t("pages.logHistory.operations.delete") },
      },
      render: (_, record) => (
        <span className="capitalize-first-letter">{record.event}</span>
      ),
    },
    {
      title: t("pages.logHistory.columns.object"),
      dataIndex: ["after", "email"],
      key: "object",
      valueType: "select",
      request: () => loadObjectOptions(loggers),
      fieldProps: {
        showSearch: true,
        filterOption: true,
      },
      search: {
        transform: (value) => ({
          userId: value,
        }),
      },
      render: (_, record: LoggerTableRow) => {
        if (record.after?.email) {
          return (
            <NavLink to={`/users/${record.after.id}`}>
              {record.after.email}
            </NavLink>
          );
        } else if (record.before?.email) {
          return record.before.email;
        } else {
          return "-";
        }
      },
    },
  ];
  const tableRequest = (
    {
      current,
      pageSize,
      ...args
    }: Record<string, string> & {
      pageSize?: number | undefined;
      current?: number | undefined;
      keyword?: string | undefined;
    },
    sorter: Record<string, SortOrder>,
  ): Promise<Partial<RequestData<LoggerTableRow>>> => {
    const mappedSorter: Record<string, SortOrder> = {};

    Object.entries(sorter).forEach(([key, value]) => {
      if (key === "user") {
        mappedSorter["userId"] = value;
      } else {
        mappedSorter[key] = value;
      }
    });

    const newParams = queryFilterParams({
      page: current ? `${current}` : "1",
      pageSize: pageSize ? `${pageSize}` : "10",
      ...args,
      ...getSorterParams(mappedSorter),
    });

    setSearchParams(
      { ...args, ...getSorterParams(mappedSorter) },
      { replace: true },
    );

    return loggers
      .fetch({
        ...newParams,
        name: newParams?.name || undefined,
      })
      .then((response) => {
        if (response) {
          const { data, total } = response;

          return {
            data: (data || []).map((item) => ({
              ...item,
              key: item.id,
            })),
            success: true,
            total,
          };
        }

        return { data: [], success: false, total: 0 };
      });
  };

  return (
    <>
      <Table
        key={`${tableKey}-${i18n.language}`}
        resetKey={() => setTableKey(Date.now())}
        className={clsx("transparent")}
        headerTitle={t("routes.logHistory")}
        toolBarRender={() => [
          <ToolBar key="toolbar" onCreate={() => setCreateOpen(true)} />,
        ]}
        request={tableRequest}
        columns={columns}
        actions={[
          [
            "view",
            ({ record }) => ({
              title: "View notification",
              onClick: (e: MouseEvent) => {
                e.preventDefault();
                setSelectedRecord(record);
                setViewOpen(true);
              },
            }),
          ],
        ]}
        search={{
          style: {
            // display: 'none',
          },
        }}
        options={{
          search: {
            name: "name",
          },
        }}
        columnsState={{
          persistenceKey: "pro-table-notification",
          persistenceType: "localStorage",
        }}
      />

      <LogHistoryViewModal
        open={viewOpen}
        onClose={() => setViewOpen(false)}
        data={selectedRecord}
      />
    </>
  );
}
